import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaVideo, FaTrash, FaEdit, FaSave, FaTimes, FaFolderOpen, FaArrowRight } from 'react-icons/fa';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import API_BASE_URL from '../config';
import './Admin.css';

const VideoManager = () => {
  const { t } = useLanguage();
  const [videos, setVideos] = useState([]); // Старые видео из /api/videos
  const [albums, setAlbums] = useState([]);
  const [editingAlbum, setEditingAlbum] = useState(null); // albumId для редактирования альбома
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [uploadToAlbumId, setUploadToAlbumId] = useState('');
  const [createNewAlbumForUpload, setCreateNewAlbumForUpload] = useState(false);
  const [newAlbumNameForUpload, setNewAlbumNameForUpload] = useState('');
  const [newAlbumDescriptionForUpload, setNewAlbumDescriptionForUpload] = useState('');
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    video: null
  });
  const [uploading, setUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [selectedVideos, setSelectedVideos] = useState([]); // Массив ID выбранных видео для массового удаления
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [currentAlbum, setCurrentAlbum] = useState(null); // Текущий открытый альбом с полными данными

  const fetchVideos = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/videos`);
      setVideos(response.data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  }, []);

  const fetchAlbums = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/albums`);
      const allAlbums = response.data || [];
      // Фильтруем только альбомы с видео (игнорируем альбомы только с фото)
      const albumsWithVideos = allAlbums.filter(album => 
        album.videos && album.videos.length > 0
      );
      setAlbums(albumsWithVideos);
      
      // Если выбран альбом, обновляем его данные
      if (selectedAlbum) {
        const updatedAlbum = allAlbums.find(a => a.id === selectedAlbum);
        if (updatedAlbum) {
          // Обновляем альбом в списке
          setAlbums(prevAlbums => {
            const index = prevAlbums.findIndex(a => a.id === selectedAlbum);
            if (index !== -1) {
              const newAlbums = [...prevAlbums];
              newAlbums[index] = updatedAlbum;
              return newAlbums;
            }
            return prevAlbums;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching albums:', error);
    }
  }, [selectedAlbum]);

  useEffect(() => {
    fetchVideos();
    fetchAlbums();
  }, [fetchVideos, fetchAlbums]);

  // Загружаем данные альбома при выборе
  useEffect(() => {
    if (selectedAlbum) {
      const fetchCurrentAlbum = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/albums`);
          const album = (response.data || []).find(a => a.id === selectedAlbum);
          if (album) {
            setCurrentAlbum(album);
          } else {
            setCurrentAlbum(null);
          }
        } catch (error) {
          console.error('Error fetching current album:', error);
          setCurrentAlbum(null);
        }
      };
      fetchCurrentAlbum();
    } else {
      setCurrentAlbum(null);
    }
  }, [selectedAlbum]);

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoForm({ ...videoForm, video: file });
    }
  };

  const handleVideoUpload = async (e) => {
    e.preventDefault();
    
    if (!videoForm.video) {
      alert(t('admin.selectFile') || 'Выберите видео для загрузки');
      return;
    }

    let targetAlbumId = uploadToAlbumId;

    // Если альбом не выбран и не создается новый - автоматически создаем новый альбом
    if (!targetAlbumId && !createNewAlbumForUpload) {
      // Создаем альбом с названием на основе даты
      const date = new Date();
      const dateStr = date.toLocaleDateString('ru-RU', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const defaultAlbumName = `Видео ${dateStr}`;
      
      try {
        const albumResponse = await axios.post(`${API_BASE_URL}/api/albums`, {
          name: defaultAlbumName,
          description: t('admin.albumCreatedAuto') || 'Создан автоматически при загрузке видео'
        });
        targetAlbumId = albumResponse.data.id;
        console.log(`✅ Album created: ${targetAlbumId}`);
        // Обновляем список альбомов после создания
        await fetchAlbums();
      } catch (error) {
        console.error('Error creating album:', error);
        alert(t('admin.albumCreateError') || 'Ошибка создания альбома');
        return;
      }
    } else if (createNewAlbumForUpload) {
      // Создаем новый альбом с указанным названием
      if (!newAlbumNameForUpload.trim()) {
        alert(t('admin.fillTitle') || 'Введите название альбома');
        return;
      }
      
      try {
        const albumResponse = await axios.post(`${API_BASE_URL}/api/albums`, {
          name: newAlbumNameForUpload.trim(),
          description: newAlbumDescriptionForUpload.trim()
        });
        targetAlbumId = albumResponse.data.id;
        await fetchAlbums();
        setCreateNewAlbumForUpload(false);
        setNewAlbumNameForUpload('');
        setNewAlbumDescriptionForUpload('');
      } catch (error) {
        console.error('Error creating album:', error);
        alert(t('admin.albumCreateError') || 'Ошибка создания альбома');
        return;
      }
    }

    // Загружаем видео в альбом
    if (!targetAlbumId) {
      alert(t('admin.selectAlbum') || 'Выберите альбом');
      return;
    }

    const formData = new FormData();
    formData.append('video', videoForm.video);
    formData.append('title', videoForm.title.trim() || videoForm.video.name.replace(/\.[^/.]+$/, ''));
    formData.append('description', videoForm.description.trim() || '');

    try {
      setUploading(true);
      setVideoUploadProgress(0);
      console.log(`📹 Uploading video to album: ${targetAlbumId}`);
      console.log(`📹 Video file:`, videoForm.video?.name);
      
      const response = await axios.post(`${API_BASE_URL}/api/albums/${targetAlbumId}/videos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setVideoUploadProgress(percentCompleted);
          }
        }
      });
      
      console.log(`✅ Video uploaded successfully:`, response.data);
      
      // Обновляем данные
      await fetchAlbums();
      await fetchVideos();
      
      // Сбрасываем форму
      setVideoForm({ title: '', description: '', video: null });
      if (document.getElementById('video-file')) {
        document.getElementById('video-file').value = '';
      }
      setUploadToAlbumId('');
      setCreateNewAlbumForUpload(false);
      setNewAlbumNameForUpload('');
      setNewAlbumDescriptionForUpload('');
      setVideoUploadProgress(0);
      
      alert(t('admin.videoUploaded') || 'Видео загружено!');
    } catch (error) {
      console.error('Error uploading video:', error);
      alert(t('admin.videoUploadError') || 'Ошибка загрузки видео');
    } finally {
      setUploading(false);
      setTimeout(() => setVideoUploadProgress(0), 1000);
    }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm(t('admin.deleteVideoConfirm') || 'Удалить это видео?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/videos/${id}`);
      setVideos(videos.filter(v => v.id !== id));
      
      // Удаляем ID из массива выбранных видео
      setSelectedVideos(prev => prev.filter(videoId => videoId !== id));
    } catch (error) {
      console.error('Error deleting video:', error);
      alert(t('admin.videoDeleteError') || 'Ошибка удаления видео');
    }
  };

  const handleUploadVideosToAlbum = async (albumId, files) => {
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      setVideoUploadProgress(0);
      
      const filesArray = Array.from(files);
      const totalFiles = filesArray.length;
      
      // Загружаем видео по одному (бэкенд принимает upload.single('video'))
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        const formData = new FormData();
        formData.append('video', file);
        
        await axios.post(`${API_BASE_URL}/api/albums/${albumId}/videos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              // Прогресс текущего файла
              const fileProgress = (progressEvent.loaded * 100) / progressEvent.total;
              // Общий прогресс: уже загруженные файлы + прогресс текущего файла
              const overallProgress = Math.round(((i / totalFiles) * 100) + (fileProgress / totalFiles));
              setVideoUploadProgress(overallProgress);
            }
          }
        });
      }
      
      await fetchAlbums();
      if (selectedAlbum === albumId) {
        // Обновляем текущий альбом
        const response = await axios.get(`${API_BASE_URL}/api/albums`);
        const updatedAlbum = (response.data || []).find(a => a.id === albumId);
        if (updatedAlbum) {
          setCurrentAlbum(updatedAlbum);
        }
      }
      setVideoUploadProgress(0);
      alert(t('admin.videosUploaded') || 'Видео загружены!');
    } catch (error) {
      console.error('Error uploading videos:', error);
      alert(t('admin.videosUploadError') || 'Ошибка загрузки видео');
    } finally {
      setUploading(false);
      setTimeout(() => setVideoUploadProgress(0), 1000);
    }
  };

  const handleDeleteVideoFromAlbum = async (albumId, videoId) => {
    if (!window.confirm(t('admin.deleteVideoConfirm') || 'Удалить это видео?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/albums/${albumId}/videos/${videoId}`);
      
      // Удаляем ID из массива выбранных видео
      setSelectedVideos(prev => prev.filter(id => id !== videoId));
      
      await fetchAlbums();
      if (selectedAlbum === albumId) {
        // Обновляем текущий альбом
        const response = await axios.get(`${API_BASE_URL}/api/albums`);
        const updatedAlbum = (response.data || []).find(a => a.id === albumId);
        if (updatedAlbum) {
          setCurrentAlbum(updatedAlbum);
        }
      }
    } catch (error) {
      console.error('Error deleting video from album:', error);
      alert(t('admin.videoDeleteError') || 'Ошибка удаления видео');
    }
  };

  const handleDeleteAlbum = async (id) => {
    if (!window.confirm(t('admin.deleteAlbumConfirm') || 'Удалить этот альбом? Все видео будут удалены.')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/albums/${id}`);
      setAlbums(albums.filter(a => a.id !== id));
      if (selectedAlbum === id) {
        setSelectedAlbum(null);
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      alert(t('admin.albumDeleteError') || 'Ошибка удаления альбома');
    }
  };

  const handleUpdateAlbum = async (albumId) => {
    try {
      const name = document.getElementById(`album-name-${albumId}`)?.value.trim();
      const description = document.getElementById(`album-description-${albumId}`)?.value.trim();

      if (!name) {
        alert(t('admin.fillTitle') || 'Введите название альбома');
        return;
      }

      await axios.put(`${API_BASE_URL}/api/albums/${albumId}`, { name, description });
      setEditingAlbum(null);
      fetchAlbums();
      alert(t('admin.albumUpdated') || 'Альбом обновлен!');
    } catch (error) {
      console.error('Error updating album:', error);
      alert(t('admin.albumUpdateError') || 'Ошибка обновления альбома');
    }
  };

  const handleToggleVideoSelection = (videoId) => {
    setSelectedVideos(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  const handleSelectAllVideos = () => {
    if (selectedAlbum) {
      const album = albums.find(a => a.id === selectedAlbum);
      if (album && album.videos) {
        if (selectAllMode && selectedVideos.length === album.videos.length) {
          setSelectedVideos([]);
          setSelectAllMode(false);
        } else {
          setSelectedVideos(album.videos.map(v => v.id));
          setSelectAllMode(true);
        }
      }
    } else {
      if (selectAllMode && selectedVideos.length === videos.length) {
        setSelectedVideos([]);
        setSelectAllMode(false);
      } else {
        setSelectedVideos(videos.map(v => v.id));
        setSelectAllMode(true);
      }
    }
  };

  const handleBulkDeleteVideos = async () => {
    if (selectedVideos.length === 0) return;
    
    if (!window.confirm(t('admin.deleteSelectedPhotosConfirm') || `Удалить ${selectedVideos.length} видео?`)) return;

    try {
      if (selectedAlbum) {
        // Удаляем из альбома
        for (const videoId of selectedVideos) {
          await axios.delete(`${API_BASE_URL}/api/albums/${selectedAlbum}/videos/${videoId}`);
        }
        await fetchAlbums();
      } else {
        // Удаляем из старой галереи
        for (const videoId of selectedVideos) {
          await axios.delete(`${API_BASE_URL}/api/videos/${videoId}`);
        }
        await fetchVideos();
      }
      setSelectedVideos([]);
      setSelectAllMode(false);
      alert(t('admin.photosDeleted') || 'Видео удалены!');
    } catch (error) {
      console.error('Error deleting videos:', error);
      alert(t('admin.videoDeleteError') || 'Ошибка удаления видео');
    }
  };

  const handleBulkMoveVideos = async (targetAlbumId) => {
    if (selectedVideos.length === 0 || !targetAlbumId) return;

    try {
      // Для видео из альбома нужно получить данные, затем создать в новом альбоме
      if (selectedAlbum) {
        const album = albums.find(a => a.id === selectedAlbum);
        if (album && album.videos) {
          for (const videoId of selectedVideos) {
            const video = album.videos.find(v => v.id === videoId);
            if (video) {
              // Получаем файл видео
              const response = await fetch(`${API_BASE_URL}${video.url}`);
              const blob = await response.blob();
              
              const formData = new FormData();
              formData.append('video', blob, video.title || 'video.mp4');
              formData.append('title', video.title);
              formData.append('description', video.description || '');
              
              await axios.post(`${API_BASE_URL}/api/albums/${targetAlbumId}/videos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
              
              // Удаляем из текущего альбома
              await axios.delete(`${API_BASE_URL}/api/albums/${selectedAlbum}/videos/${videoId}`);
            }
          }
        }
      } else {
        // Для старых видео
        for (const videoId of selectedVideos) {
          const video = videos.find(v => v.id === videoId);
          if (video) {
            const response = await fetch(`${API_BASE_URL}${video.url}`);
            const blob = await response.blob();
            
            const formData = new FormData();
            formData.append('video', blob, video.title || 'video.mp4');
            formData.append('title', video.title);
            formData.append('description', video.description || '');
            
            await axios.post(`${API_BASE_URL}/api/albums/${targetAlbumId}/videos`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            await axios.delete(`${API_BASE_URL}/api/videos/${videoId}`);
          }
        }
      }
      
      await fetchAlbums();
      await fetchVideos();
      setSelectedVideos([]);
      setSelectAllMode(false);
      setShowMoveDialog(false);
      alert(t('admin.photosMoved') || 'Видео перенесены!');
    } catch (error) {
      console.error('Error moving videos:', error);
      alert(t('admin.photoMoveError') || 'Ошибка переноса видео');
    }
  };

  return (
    <div className="admin-section">
      <div style={{ marginBottom: '2rem' }}>
        {selectedAlbum ? (
          <div>
            {(() => {
              const album = currentAlbum || albums.find(a => a.id === selectedAlbum);
              if (!album) {
                // Показываем загрузку или пустое состояние
                return <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '2rem' }}>Загрузка...</p>;
              }
              
              return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div>
                      <h3>{album.name}</h3>
                      {album.description && <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{album.description}</p>}
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                        {album.videos?.length || 0} {album.videos?.length === 1 ? (t('admin.video') || 'видео') : (t('admin.videos') || 'видео')}
                      </p>
                    </div>
                    <button onClick={() => setSelectedAlbum(null)} style={{ padding: '0.5rem 1rem', background: 'rgba(149, 165, 166, 0.2)', color: '#95a5a6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                      ← {t('admin.backToAlbums') || 'Back to albums'}
                    </button>
                  </div>
                  
                  {/* VK-style upload area */}
                  <div style={{ 
                    border: '2px dashed rgba(231, 76, 60, 0.3)', 
                    borderRadius: '12px', 
                    padding: '2rem', 
                    textAlign: 'center',
                    marginBottom: '2rem',
                    background: 'rgba(231, 76, 60, 0.05)',
                    transition: 'all 0.3s',
                    cursor: 'pointer'
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(231, 76, 60, 0.6)'; }}
                  onDragLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(231, 76, 60, 0.3)'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = 'rgba(231, 76, 60, 0.3)';
                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      handleUploadVideosToAlbum(selectedAlbum, e.dataTransfer.files);
                    }
                  }}
                  >
                    <label style={{ cursor: 'pointer', display: 'block' }}>
                      <div style={{ 
                        fontSize: '3rem', 
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem',
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, rgba(231, 76, 60, 0.25), rgba(192, 57, 43, 0.25))',
                        borderRadius: '50%',
                        boxShadow: '0 4px 20px rgba(231, 76, 60, 0.4), inset 0 0 20px rgba(231, 76, 60, 0.1)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '2px solid rgba(231, 76, 60, 0.4)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                        e.currentTarget.style.boxShadow = '0 6px 25px rgba(231, 76, 60, 0.6), inset 0 0 25px rgba(231, 76, 60, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(231, 76, 60, 0.4), inset 0 0 20px rgba(231, 76, 60, 0.1)';
                      }}
                      >
                        <FaVideo style={{ 
                          color: '#e74c3c', 
                          filter: 'drop-shadow(0 2px 6px rgba(231, 76, 60, 0.6))',
                          fontSize: '2.5rem'
                        }} />
                      </div>
                      <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: '#fff', fontWeight: '600', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
                        Upload video to album
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                        Drag and drop photos here or click to select
                      </div>
                      <input
                        type="file"
                        accept="video/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleUploadVideosToAlbum(selectedAlbum, e.target.files);
                          }
                        }}
                      />
                    </label>
                  </div>

                  {/* Прогресс-бар загрузки для drag & drop */}
                  {uploading && (
                    <div className="upload-progress-container" style={{ marginBottom: '1rem' }}>
                      <div className="upload-progress-bar">
                        <div 
                          className="upload-progress-fill" 
                          style={{ width: `${videoUploadProgress}%` }}
                        ></div>
                      </div>
                      <div className="upload-progress-text">
                        {videoUploadProgress}% {videoUploadProgress < 100 ? t('admin.uploading') : t('admin.processing')}
                      </div>
                    </div>
                  )}

                  {/* Панель управления массовым удалением */}
                  {album.videos && album.videos.length > 0 && (
                    <div className="bulk-actions-panel">
                      <div className="bulk-actions-left">
                        <input
                          type="checkbox"
                          checked={selectAllMode && selectedVideos.length === album.videos.length}
                          onChange={handleSelectAllVideos}
                          className="bulk-checkbox"
                        />
                        <span className="bulk-selection-text">
                          {selectedVideos.length > 0 ? `Выбрано: ${selectedVideos.length}` : 'Выбрать все'}
                        </span>
                      </div>
                      {selectedVideos.length > 0 && (
                        <div className="bulk-actions-right">
                          {albums.filter(a => a.id !== selectedAlbum).length > 0 && (
                            <button
                              onClick={() => setShowMoveDialog(true)}
                              className="move-btn"
                              style={{
                                padding: '0.5rem 1rem',
                                background: 'rgba(79, 172, 254, 0.15)',
                                color: '#4facfe',
                                border: '1px solid rgba(79, 172, 254, 0.3)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontWeight: '500',
                                fontSize: '0.9rem'
                              }}
                            >
                              <FaArrowRight /> Перенести ({selectedVideos.length})
                            </button>
                          )}
                          <button
                            onClick={handleBulkDeleteVideos}
                            className="delete-btn-bulk"
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(231, 76, 60, 0.15)',
                              color: '#e74c3c',
                              border: '1px solid rgba(231, 76, 60, 0.3)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontWeight: '500',
                              fontSize: '0.9rem'
                            }}
                          >
                            <FaTrash /> Удалить ({selectedVideos.length})
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="items-list">
                    {album.videos && album.videos.length > 0 ? (
                      [...album.videos].sort((a, b) => {
                        // Сортируем по дате загрузки (последние первыми)
                        const dateA = new Date(a.uploadedAt || a.createdAt || 0);
                        const dateB = new Date(b.uploadedAt || b.createdAt || 0);
                        return dateB - dateA; // Обратный порядок (новые первыми)
                      }).map((video) => (
                        <motion.div key={video.id} className="item-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                          <div className="item-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem' }}>
                            <input
                              type="checkbox"
                              checked={selectedVideos.includes(video.id)}
                              onChange={() => handleToggleVideoSelection(video.id)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }}
                            />
                            <div className="item-thumbnail">
                              <video 
                                src={`${API_BASE_URL}${video.url}`}
                                muted
                                preload="metadata"
                                onLoadedMetadata={(e) => {
                                  e.target.currentTime = 1;
                                }}
                                onTimeUpdate={(e) => {
                                  if (e.target.currentTime >= 1) {
                                    e.target.pause();
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                              />
                            </div>
                          </div>
                          <div className="item-footer" style={{ justifyContent: 'flex-end' }}>
                            <div className="item-actions">
                              <button
                                className="delete-btn"
                                onClick={() => handleDeleteVideoFromAlbum(selectedAlbum, video.id)}
                                title={t('admin.delete') || 'Удалить'}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '2rem' }}>
                        {t('admin.noVideos') || 'В альбоме пока нет видео'}
                      </p>
                    )}
                  </div>

                  {/* Диалог переноса видео */}
                  {showMoveDialog && selectedVideos.length > 0 && selectedAlbum && (
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000
                    }}
                    onClick={() => setShowMoveDialog(false)}
                    >
                      <div style={{
                        background: 'rgba(30, 30, 30, 0.95)',
                        padding: '2rem',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        minWidth: '400px',
                        maxWidth: '500px'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      >
                        <h3 style={{ marginBottom: '1rem', color: '#fff' }}>
                          Перенести {selectedVideos.length} {selectedVideos.length === 1 ? 'видео' : 'видео'} в альбом
                        </h3>
                        <select
                          id="bulk-move-target-album"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '1rem',
                            marginBottom: '1rem'
                          }}
                        >
                          <option value="">Выберите альбом</option>
                          {albums.filter(a => a.id !== selectedAlbum).map(a => (
                            <option key={a.id} value={a.id}>
                              {a.name} ({(a.videos?.length || 0)} {t('admin.videos') || 'видео'})
                            </option>
                          ))}
                        </select>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setShowMoveDialog(false)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(149, 165, 166, 0.2)',
                              color: '#95a5a6',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                          >
                            Отмена
                          </button>
                          <button
                            onClick={() => {
                              const targetAlbumId = document.getElementById('bulk-move-target-album').value;
                              if (!targetAlbumId) {
                                alert('Выберите альбом');
                                return;
                              }
                              handleBulkMoveVideos(targetAlbumId);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(79, 172, 254, 0.3)',
                              color: '#4facfe',
                              border: '1px solid rgba(79, 172, 254, 0.4)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            Перенести
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="items-list">
            {!selectedAlbum && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2>{t('admin.videoAlbums') || 'Video Albums'}</h2>
              </div>
            )}
            {albums.map((album) => (
              <motion.div key={album.id} className="item-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                {editingAlbum === album.id ? (
                  <div className="edit-form">
                    <input
                      type="text"
                      defaultValue={album.name}
                      id={`album-name-${album.id}`}
                      placeholder={t('admin.albumName') || 'Название альбома'}
                    />
                    <textarea
                      defaultValue={album.description || ''}
                      id={`album-description-${album.id}`}
                      placeholder={t('admin.albumDescription') || 'Описание альбома (необязательно)'}
                    />
                    <div className="edit-actions">
                      <button
                        className="save-btn"
                        onClick={() => handleUpdateAlbum(album.id)}
                      >
                        <FaSave /> {t('admin.save') || 'Сохранить'}
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={() => setEditingAlbum(null)}
                      >
                        <FaTimes /> {t('admin.cancel') || 'Отмена'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="item-info">
                      {album.coverPhoto ? (
                        <div className="item-thumbnail">
                          <img src={`${API_BASE_URL}${album.coverPhoto}`} alt={album.name} className="gallery-item-thumbnail" />
                        </div>
                      ) : album.videos && album.videos.length > 0 ? (
                        <div className="item-thumbnail">
                          <video 
                            src={`${API_BASE_URL}${album.videos[0].url}`}
                            muted
                            preload="metadata"
                            onLoadedMetadata={(e) => {
                              e.target.currentTime = 1;
                            }}
                            onTimeUpdate={(e) => {
                              if (e.target.currentTime >= 1) {
                                e.target.pause();
                              }
                            }}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '8px'
                            }}
                          />
                        </div>
                      ) : null}
                      <div className="item-text-info">
                        <h3>{album.name}</h3>
                        {album.description && <p>{album.description}</p>}
                        <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                          {(album.videos?.length || 0)} {(album.videos?.length || 0) === 1 ? (t('admin.video') || 'видео') : (t('admin.videos') || 'видео')}
                        </p>
                      </div>
                    </div>
                    <div className="item-footer">
                      <div className="item-actions">
                        <button className="edit-btn" onClick={() => setSelectedAlbum(album.id)}>
                          <FaFolderOpen /> {t('admin.open') || 'Открыть'}
                        </button>
                        <button 
                          className="edit-btn" 
                          onClick={() => setEditingAlbum(album.id)}
                          title={t('admin.edit') || 'Редактировать'}
                        >
                          <FaEdit />
                        </button>
                        <button className="delete-btn" onClick={() => handleDeleteAlbum(album.id)} title={t('admin.delete') || 'Удалить'}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            ))}
            {albums.length === 0 && (
              <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '2rem' }}>
                {t('admin.noAlbums') || 'Альбомов пока нет. Создайте первый альбом!'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Video Upload Section */}
      <div style={{ marginTop: '3rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '2rem' }}>
        <h2>{t('admin.uploadVideo') || 'Загрузка Видео'}</h2>
        <form onSubmit={handleVideoUpload} className="upload-form">
          {/* Выбор альбома или создание нового */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
              {t('admin.selectAlbum') || 'Выберите альбом'} <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>({t('admin.autoCreateIfEmpty') || 'или будет создан автоматически'})</span>
            </label>
            <select
              value={createNewAlbumForUpload ? 'new' : uploadToAlbumId}
              onChange={(e) => {
                if (e.target.value === 'new') {
                  setCreateNewAlbumForUpload(true);
                  setUploadToAlbumId('');
                } else if (e.target.value === '') {
                  setCreateNewAlbumForUpload(false);
                  setUploadToAlbumId('');
                } else {
                  setCreateNewAlbumForUpload(false);
                  setUploadToAlbumId(e.target.value);
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: '1rem'
              }}
            >
              <option value="">{t('admin.autoCreateAlbum') || 'Создать альбом автоматически'}</option>
              <option value="new">{t('admin.createNewAlbum') || '+ Создать новый альбом'}</option>
              {albums.map(album => (
                <option key={album.id} value={album.id}>
                  {album.name} ({(album.videos?.length || 0)} {t('admin.videos') || 'видео'})
                </option>
              ))}
            </select>

            {/* Форма создания нового альбома */}
            {createNewAlbumForUpload && (
              <div style={{ 
                padding: '1rem', 
                background: 'rgba(46, 204, 113, 0.1)', 
                borderRadius: '8px', 
                border: '1px solid rgba(46, 204, 113, 0.3)',
                marginBottom: '1rem'
              }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {t('admin.albumName') || 'Название альбома'} *
                </label>
                <input
                  type="text"
                  value={newAlbumNameForUpload}
                  onChange={(e) => setNewAlbumNameForUpload(e.target.value)}
                  placeholder={t('admin.albumName') || 'Название альбома (например: Tomorrowland 2026)'}
                  required={createNewAlbumForUpload}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem',
                    marginBottom: '0.75rem'
                  }}
                />
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {t('admin.albumDescription') || 'Описание альбома (необязательно)'}
                </label>
                <textarea
                  value={newAlbumDescriptionForUpload}
                  onChange={(e) => setNewAlbumDescriptionForUpload(e.target.value)}
                  placeholder={t('admin.albumDescription') || 'Описание альбома (необязательно)'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </div>
            )}
          </div>

          <input
            type="text"
            value={videoForm.title}
            onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
            placeholder={t('admin.videoTitle') || 'Название видео'}
            style={{ marginBottom: '1rem' }}
          />
          <textarea
            value={videoForm.description}
            onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
            placeholder={t('admin.videoDescription') || 'Описание видео (необязательно)'}
            style={{ marginBottom: '1rem' }}
          />
          <label className="file-input-label" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <span style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(231, 76, 60, 0.1)',
              marginRight: '0.75rem',
              border: '1px solid rgba(231, 76, 60, 0.2)',
              flexShrink: 0
            }}>
              <FaVideo style={{ color: '#e74c3c', fontSize: '1.1rem' }} />
            </span>
            <span style={{ flex: 1 }}>{t('admin.selectVideo') || 'Выбрать видео'}</span>
            <input 
              type="file" 
              id="video-file" 
              accept="video/*" 
              onChange={handleVideoFileChange}
            />
            {videoForm.video && (
              <span>{videoForm.video.name}</span>
            )}
          </label>
          
          {uploading && (
            <div className="upload-progress-container">
              <div className="upload-progress-bar">
                <div 
                  className="upload-progress-fill" 
                  style={{ width: `${videoUploadProgress}%` }}
                ></div>
              </div>
              <div className="upload-progress-text">
                {videoUploadProgress}% {videoUploadProgress < 100 ? t('admin.uploading') : t('admin.processing')}
              </div>
            </div>
          )}
          
          <button type="submit" disabled={uploading}>
            {uploading ? t('admin.uploading') : (t('admin.uploadVideo') || 'Загрузить видео')}
          </button>
        </form>

        {/* Список старых видео (только если есть элементы) */}
        {videos.length > 0 && (
          <>
            <h2 style={{ marginTop: '2rem' }}>{t('admin.videos') || 'Видео'}</h2>
            {/* Панель управления массовым удалением */}
            <div className="bulk-actions-panel">
              <div className="bulk-actions-left">
                <input
                  type="checkbox"
                  checked={selectAllMode && selectedVideos.length === videos.length}
                  onChange={handleSelectAllVideos}
                  className="bulk-checkbox"
                />
                <span className="bulk-selection-text">
                  {selectedVideos.length > 0 ? `Выбрано: ${selectedVideos.length}` : 'Выбрать все'}
                </span>
              </div>
              {selectedVideos.length > 0 && (
                <div className="bulk-actions-right">
                  {albums.length > 0 && (
                    <button
                      onClick={() => setShowMoveDialog(true)}
                      className="move-btn"
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(79, 172, 254, 0.15)',
                        color: '#4facfe',
                        border: '1px solid rgba(79, 172, 254, 0.3)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontWeight: '500',
                        fontSize: '0.9rem'
                      }}
                    >
                      <FaArrowRight /> Перенести ({selectedVideos.length})
                    </button>
                  )}
                  <button
                    onClick={handleBulkDeleteVideos}
                    className="delete-btn-bulk"
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(231, 76, 60, 0.15)',
                      color: '#e74c3c',
                      border: '1px solid rgba(231, 76, 60, 0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontWeight: '500',
                      fontSize: '0.9rem'
                    }}
                  >
                    <FaTrash /> Удалить ({selectedVideos.length})
                  </button>
                </div>
              )}
            </div>
            {/* Диалог переноса видео для старой галереи */}
            {showMoveDialog && selectedVideos.length > 0 && !selectedAlbum && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setShowMoveDialog(false)}
              >
                <div style={{
                  background: 'rgba(30, 30, 30, 0.95)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  minWidth: '400px',
                  maxWidth: '500px'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <h3 style={{ marginBottom: '1rem', color: '#fff' }}>
                    Перенести {selectedVideos.length} {selectedVideos.length === 1 ? 'видео' : 'видео'} в альбом
                  </h3>
                  <select
                    id="bulk-move-target-album-legacy"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1rem',
                      marginBottom: '1rem'
                    }}
                  >
                    <option value="">Выберите альбом</option>
                    {albums.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({(a.videos?.length || 0)} {t('admin.videos') || 'видео'})
                      </option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowMoveDialog(false)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(149, 165, 166, 0.2)',
                        color: '#95a5a6',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={() => {
                        const targetAlbumId = document.getElementById('bulk-move-target-album-legacy').value;
                        if (!targetAlbumId) {
                          alert('Выберите альбом');
                          return;
                        }
                        handleBulkMoveVideos(targetAlbumId);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(79, 172, 254, 0.3)',
                        color: '#4facfe',
                        border: '1px solid rgba(79, 172, 254, 0.4)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Перенести
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="items-list">
              {videos.map((video) => (
                <motion.div key={video.id} className="item-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <div className="item-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedVideos.includes(video.id)}
                      onChange={() => handleToggleVideoSelection(video.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div className="item-thumbnail">
                      <video 
                        src={`${API_BASE_URL}${video.url}`}
                        muted
                        preload="metadata"
                        onLoadedMetadata={(e) => {
                          e.target.currentTime = 1;
                        }}
                        onTimeUpdate={(e) => {
                          if (e.target.currentTime >= 1) {
                            e.target.pause();
                          }
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    </div>
                  </div>
                  <div className="item-footer" style={{ justifyContent: 'flex-end' }}>
                    <div className="item-actions">
                      <button className="delete-btn" onClick={() => handleDeleteVideo(video.id)} title={t('admin.delete') || 'Удалить'}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoManager;
